import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { applyActionCode } from "firebase/auth";
import { auth, db } from "../../lib/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { CheckCircle, XCircle, Loader } from "lucide-react";

export default function ActionHandler() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState("");
  const [countdown, setCountdown] = useState(2);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleAction = async () => {
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');
      const continueUrl = searchParams.get('continueUrl');

      console.log('[ActionHandler] Mode:', mode);
      console.log('[ActionHandler] Continue URL:', continueUrl);

      // Handle Email Verification
      if (mode === 'verifyEmail' && oobCode) {
        try {
          // Apply the verification code
          await applyActionCode(auth, oobCode);
          console.log('[ActionHandler] ✅ Email verified successfully');

          // Get current user or wait for auth state
          const currentUser = auth.currentUser;
          
          let redirectUrl = 'https://aiforyou.web.app/'; // Default redirect to home
          
          if (currentUser) {
            // Reload user to get updated emailVerified status
            await currentUser.reload();
            
            // Update user document
            const userRef = doc(db, "users", currentUser.uid);
            const userDoc = await getDoc(userRef);
            
            if (userDoc.exists()) {
              await updateDoc(userRef, {
                emailVerified: true,
                updatedAt: new Date()
              });
              
              const signupSource = userDoc.data()?.signupSource;
              console.log('[ActionHandler] Signup source:', signupSource);

              // Determine redirect based on signup source - redirect to home page
              if (signupSource === 'coursedee') {
                redirectUrl = 'https://aiforyou.web.app/';
              } else if (signupSource === 'salepage') {
                redirectUrl = 'https://saledi.web.app/pricing';
              } else {
                redirectUrl = 'https://aiforyou.web.app/';
              }
            }
          } else {
            // User not logged in, try to determine from continueUrl or hostname
            if (continueUrl) {
              const url = new URL(continueUrl);
              const hostname = url.hostname;
              
              if (hostname.includes('aiforyou')) {
                redirectUrl = 'https://aiforyou.web.app/';
              } else if (hostname.includes('salepage')) {
                redirectUrl = 'https://saledi.web.app/pricing';
              } else {
                redirectUrl = 'https://aiforyou.web.app/';
              }
            } else {
              // Check current hostname
              const hostname = window.location.hostname;
              if (hostname.includes('aiforyou')) {
                redirectUrl = 'https://aiforyou.web.app/';
              } else if (hostname.includes('salepage')) {
                redirectUrl = 'https://saledi.web.app/pricing';
              } else {
                redirectUrl = 'https://aiforyou.web.app/';
              }
            }
          }

          setStatus('success');
          setMessage("ยืนยันอีเมลสำเร็จ! กำลังนำคุณกลับไปหน้าแรก...");

          // Countdown timer
          let timeLeft = 2;
          const countdownInterval = setInterval(() => {
            timeLeft--;
            setCountdown(timeLeft);
            if (timeLeft <= 0) {
              clearInterval(countdownInterval);
            }
          }, 1000);

          // Auto redirect after 2 seconds
          setTimeout(() => {
            if (redirectUrl.startsWith('http')) {
              window.location.href = redirectUrl;
            } else {
              navigate(redirectUrl);
            }
          }, 2000);

        } catch (err: any) {
          console.error('[ActionHandler] Email verification error:', err);
          setStatus('error');
          
          if (err.code === 'auth/invalid-action-code') {
            setMessage("ลิงก์หมดอายุหรือถูกใช้งานแล้ว กรุณาขอลิงก์ใหม่");
          } else if (err.code === 'auth/expired-action-code') {
            setMessage("ลิงก์หมดอายุแล้ว กรุณาขอลิงก์ใหม่");
          } else {
            setMessage("เกิดข้อผิดพลาด: " + (err.message || "กรุณาลองใหม่อีกครั้ง"));
          }
        }
        return;
      }

      // Handle Password Reset (future)
      if (mode === 'resetPassword' && oobCode) {
        // TODO: Implement password reset handler
        navigate('/reset-password?oobCode=' + oobCode);
        return;
      }

      // If no valid mode, show error
      setStatus('error');
      setMessage("ลิงก์ไม่ถูกต้องหรือหมดอายุแล้ว");
    };

    handleAction();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-blue-100">
              <Loader className="w-12 h-12 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">กำลังยืนยันอีเมล...</h2>
            <p className="text-gray-600">กรุณารอสักครู่</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-green-100 animate-bounce">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-3">ยืนยันอีเมลสำเร็จ!</h2>
            <p className="text-gray-600 mb-6">กำลังนำคุณกลับไปหน้าแรก...</p>
            
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-blue-200"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-blue-600">{countdown}</span>
                </div>
                <svg className="absolute inset-0 w-16 h-16 -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-blue-600"
                    strokeDasharray={`${(countdown / 2) * 176} 176`}
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
            
            <p className="text-sm text-gray-500">กำลัง redirect อัตโนมัติ...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-red-100">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">เกิดข้อผิดพลาด</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              กลับสู่หน้าเข้าสู่ระบบ
            </button>
          </>
        )}
      </div>
    </div>
  );
}
